import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import { toast } from '@kognitos/lattice';

import {
  getAutomationListThreadsQueryKey,
  useAutomationCreateThread,
  useAutomationListThreads,
} from '@/generated/hooks/rain/v1/automation_scoped/thread_service';
import { captureException } from '@/modules/sentry';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import { errorHandler } from '@/shared/utils/error-handler';

import { RAIN_QUILL_AGENT_ID } from '../agents/quill';

interface IUseRainThreadParams {
  /** Automation the threads belong to. */
  automationId: string;
  /**
   * Whether thread resolution should run. Callers pass the same predicate that
   * decides whether the Rain chat is on screen (Rain automation + chat
   * visible). When false, no threads are listed or created.
   */
  enabled: boolean;
  /**
   * Seeds `rainThreadId` on this hook's very first render for this
   * `automationId`, before `useAutomationListThreads` has even resolved —
   * e.g. a thread created just before navigating here from outside the
   * editor (see useUpgradeAutomationManifest). Without this, "latest
   * existing thread" is resolved from whatever the list query returns on
   * mount, which can race a thread created moments earlier and lose: this
   * hook's own lazy-create effect only skips while `isLoadingRainThreads`
   * is true, so if a stale/incomplete list resolves first, it creates a
   * second, empty thread before the intended one is visible. Seeding the
   * initial `selectedRainThreadId` (the precedence-highest source) instead
   * of waiting for a later effect closes that race entirely — the intended
   * thread wins on the first render, no discovery step involved.
   */
  initialThreadId?: string;
}

interface IUseRainThreadResult {
  /**
   * Thread the Rain chat should bind to: the user's pick, else the latest
   * existing thread, else a thread created lazily by this hook. `undefined`
   * only while resolving or after a creation failure.
   */
  rainThreadId: string | undefined;
  /** Bubble up a thread picked/created inside the chat's thread selector. */
  setSelectedRainThreadId: (threadId: string) => void;
  /** True while listing existing threads, waiting to create, or creating the first one. */
  isResolvingRainThread: boolean;
  /** True after thread creation failed without a usable thread id. */
  hasRainThreadError: boolean;
  /** Re-attempt resolution after a failure. */
  retryResolveRainThread: () => void;
}

/**
 * Owns the Rain thread id for an automation. A Rain (Quill V2) automation must
 * always have a thread to bind the chat to, so this hook resolves one from the
 * existing threads and lazily creates one when none exists. The chat surface
 * therefore depends only on the automation *kind*, never on a thread already
 * being present.
 *
 * Resolution precedence: user selection > latest existing thread > lazily
 * created thread.
 */
export function useRainThread({
  automationId,
  enabled,
  initialThreadId,
}: IUseRainThreadParams): IUseRainThreadResult {
  const { t } = useTranslation('rain');
  const queryClient = useQueryClient();
  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );
  const currentWorkspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const { data: rainThreadsResponse, isLoading: isLoadingRainThreads } =
    useAutomationListThreads(
      currentOrganizationId,
      currentWorkspaceId,
      automationId,
      RAIN_QUILL_AGENT_ID,
      undefined,
      { query: { enabled } },
    );

  const latestRainThreadId = useMemo(() => {
    const threads = rainThreadsResponse?.data.threads ?? [];
    if (threads.length === 0) return undefined;

    return threads
      .filter(thread => thread.variables?.automationId === automationId)
      .sort((a, b) =>
        (b.updated_at ?? b.created_at ?? '').localeCompare(
          a.updated_at ?? a.created_at ?? '',
        ),
      )[0]?.thread_id;
  }, [automationId, rainThreadsResponse?.data.threads]);

  // Latest thread seeds the initial selection so the chat has a thread to bind
  // to on first paint. Once the user picks or creates a thread inside the
  // selector, that pick overrides the latest until they switch automation.
  // `initialThreadId` (present only on a fresh mount for this automationId —
  // see the param doc) takes precedence over even that.
  const [selectedRainThreadId, setSelectedRainThreadId] = useState<
    string | undefined
  >(initialThreadId);
  // Thread created by this hook when the automation had none.
  const [createdRainThreadId, setCreatedRainThreadId] = useState<
    string | undefined
  >(undefined);
  const [hasFailedCreate, setHasFailedCreate] = useState(false);

  // Reset per-automation selection/creation state when moving to a
  // different automation — but not on this hook's initial mount, which
  // would otherwise immediately wipe out `initialThreadId` right after
  // `useState` seeds it.
  const previousAutomationIdRef = useRef(automationId);
  // eslint-disable-next-line no-restricted-syntax -- reason: Reset per-automation selection/creation state when moving to a different automation
  useEffect(() => {
    if (previousAutomationIdRef.current === automationId) return;
    previousAutomationIdRef.current = automationId;
    setSelectedRainThreadId(undefined);
    setCreatedRainThreadId(undefined);
    setHasFailedCreate(false);
  }, [automationId]);

  const rainThreadId =
    selectedRainThreadId ?? latestRainThreadId ?? createdRainThreadId;

  const { mutateAsync: createRainThreadAsync } = useAutomationCreateThread();

  // Guards against duplicate creates: records the automation we've already
  // kicked off a create for (also tames React StrictMode's double effect run).
  const createInitiatedForRef = useRef<string | null>(null);

  // Lazily create a thread once the list has resolved with none. Keyed on
  // automationId so each automation creates at most one thread per attempt.
  // eslint-disable-next-line no-restricted-syntax -- reason: Lazily create a Rain thread (a network side effect) when the automation has none
  useEffect(() => {
    if (!enabled || isLoadingRainThreads || rainThreadId || hasFailedCreate) {
      return;
    }
    if (createInitiatedForRef.current === automationId) {
      return;
    }
    createInitiatedForRef.current = automationId;

    void (async () => {
      try {
        const response = await createRainThreadAsync({
          organization: currentOrganizationId,
          workspace: currentWorkspaceId,
          automation: automationId,
          agentId: RAIN_QUILL_AGENT_ID,
          data: {
            variables: {
              organizationId: currentOrganizationId,
              workspaceId: currentWorkspaceId,
              automationId,
            },
          },
        });
        // Bail out if the automation changed while the create was in flight;
        // otherwise we'd bind the new automation's chat to the stale thread.
        if (createInitiatedForRef.current !== automationId) return;
        const nextThreadId = response.data?.thread_id;
        if (nextThreadId) {
          setCreatedRainThreadId(nextThreadId);
          queryClient.invalidateQueries({
            queryKey: getAutomationListThreadsQueryKey(
              currentOrganizationId,
              currentWorkspaceId,
              automationId,
              RAIN_QUILL_AGENT_ID,
            ),
          });
        } else {
          // No id came back — allow a retry rather than spinning forever.
          createInitiatedForRef.current = null;
          setHasFailedCreate(true);
        }
      } catch (error) {
        // Ignore failures from a create we've since abandoned for another
        // automation — the reset effect already cleared this automation's state.
        if (createInitiatedForRef.current !== automationId) return;
        // Re-arm so the user can retry the create.
        createInitiatedForRef.current = null;
        setHasFailedCreate(true);
        errorHandler(error, () => {
          toast.error(t('rain.error.createThread'));
          captureException(error as Error, {
            tags: { module: 'rain-chat', action: 'ensure_thread' },
          });
        });
      }
    })();
  }, [
    enabled,
    isLoadingRainThreads,
    rainThreadId,
    hasFailedCreate,
    automationId,
    createRainThreadAsync,
    currentOrganizationId,
    currentWorkspaceId,
    queryClient,
    t,
  ]);

  const retryResolveRainThread = useCallback(() => {
    createInitiatedForRef.current = null;
    setHasFailedCreate(false);
  }, []);

  const isResolvingRainThread = enabled && !rainThreadId && !hasFailedCreate;

  const hasRainThreadError = enabled && !rainThreadId && hasFailedCreate;

  return {
    rainThreadId,
    setSelectedRainThreadId,
    isResolvingRainThread,
    hasRainThreadError,
    retryResolveRainThread,
  };
}
